/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import APIManager from "../../Services/ApiManager/ApiManager";
import PopupDialog from "../PopupDialog";
import "./OemAdminAppDependency.css";

const ANDROID_PACKAGE_REGEX = /^[a-zA-Z]\w*(\.[a-zA-Z]\w*)+$/;
const isValidPackageId = (id) => {
  const t = id.trim();
  return t.length > 0 && t.length <= 200 && ANDROID_PACKAGE_REGEX.test(t);
};

function OemAdminAppDependency({ onSave }) {
  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails,
  );

  const [dependencies, setDependencies] = useState([]);
  const [packageId, setPackageId] = useState("");
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [manualVersionCode, setManualVersionCode] = useState("");
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showPackageDropdown, setShowPackageDropdown] = useState(false);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [availableVersions, setAvailableVersions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPackageFormatInfo, setShowPackageFormatInfo] = useState(false);
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Inline edit state
  const [editIndex, setEditIndex] = useState(null);
  const [editPackageId, setEditPackageId] = useState("");
  const [editVersionCode, setEditVersionCode] = useState("");
  const [editSearchResults, setEditSearchResults] = useState([]);
  const [showEditPackageDropdown, setShowEditPackageDropdown] = useState(false);
  const [showEditVersionDropdown, setShowEditVersionDropdown] = useState(false);
  const [editAvailableVersions, setEditAvailableVersions] = useState([]);
  const [isEditSearching, setIsEditSearching] = useState(false);

  const packageDropdownRef = useRef(null);
  const versionDropdownRef = useRef(null);
  const editPackageDropdownRef = useRef(null);
  const editVersionDropdownRef = useRef(null);

  useEffect(() => {
    if (submissionData) {
      setDependencies(submissionData.portfolioContentAppDependencyInfo || []);
    }
  }, [submissionData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        packageDropdownRef.current &&
        !packageDropdownRef.current.contains(event.target)
      ) {
        setShowPackageDropdown(false);
      }
      if (
        versionDropdownRef.current &&
        !versionDropdownRef.current.contains(event.target)
      ) {
        setShowVersionDropdown(false);
      }
      if (
        editPackageDropdownRef.current &&
        !editPackageDropdownRef.current.contains(event.target)
      ) {
        setShowEditPackageDropdown(false);
      }
      if (
        editVersionDropdownRef.current &&
        !editVersionDropdownRef.current.contains(event.target)
      ) {
        setShowEditVersionDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchPackages = async (text) => {
    if (text.length < 2) return [];
    const portfolioId = sessionStorage.getItem("portfolioId");
    const locale = sessionStorage.getItem("currentLanguage") || "en";
    const results = await APIManager.searchDependencyApps(
      portfolioId,
      text,
      locale,
    );
    return Array.isArray(results) ? results : [];
  };

  const handlePackageInputChange = async (e) => {
    const value = e.target.value;
    setPackageId(value);
    setSelectedVersion(null);
    setAvailableVersions([]);
    setIsManualEntry(false);

    if (value.length < 2) {
      setSearchResults([]);
      setShowPackageDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchPackages(value);
      setSearchResults(results);
      setShowPackageDropdown(true);
      setIsManualEntry(results.length === 0);
    } catch {
      setSearchResults([]);
      setShowPackageDropdown(true);
      setIsManualEntry(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectPackage = (result) => {
    setPackageId(result.packageId);
    setShowPackageDropdown(false);
    setIsManualEntry(false);
    const versions = searchResults
      .filter((r) => r.packageId === result.packageId)
      .map((r) => ({
        versionCode: r.versionCode,
        contentVersion: r.contentVersion,
      }));
    setAvailableVersions(versions);
    setSelectedVersion(null);
  };

  const handleVersionSelect = (versionCode) => {
    setSelectedVersion(versionCode);
    setShowVersionDropdown(false);
  };

  const isAddEnabled =
    isValidPackageId(packageId) && (selectedVersion !== null || isManualEntry);
  const isPackageInvalid =
    packageId.trim() !== "" && !isValidPackageId(packageId);

  const handleAdd = async () => {
    if (!isAddEnabled || isSaving) return;

    setIsSaving(true);
    try {
      const versionToSave =
        selectedVersion === null
          ? Number(manualVersionCode.trim())
          : selectedVersion;

      const updatedDependencies = [
        ...dependencies,
        { packageId: packageId.trim(), versionCode: versionToSave },
      ];
      await saveDependencies(updatedDependencies);
      setDependencies(updatedDependencies);
      setPackageId("");
      setSelectedVersion(null);
      setAvailableVersions([]);
      setIsManualEntry(false);
      setManualVersionCode("");
    } catch (error) {
      setErrorMessage(
        error?.message || "Failed to save app dependency. Please try again.",
      );
      setOpenErrorPopup(true);
    } finally {
      setIsSaving(false);
    }
  };

  // Inline edit handlers
  const handleEdit = (index) => {
    const dep = dependencies[index];
    setEditIndex(index);
    setEditPackageId(dep.packageId);
    setEditVersionCode(String(dep.versionCode));
    setEditAvailableVersions([]);
    setEditSearchResults([]);
    setShowEditPackageDropdown(false);
    setShowEditVersionDropdown(false);
  };

  const handleEditPackageInputChange = async (e) => {
    const value = e.target.value;
    setEditPackageId(value);
    setEditVersionCode("");
    setEditAvailableVersions([]);

    if (value.length < 2) {
      setEditSearchResults([]);
      setShowEditPackageDropdown(false);
      return;
    }

    setIsEditSearching(true);
    try {
      const results = await searchPackages(value);
      setEditSearchResults(results);
      setShowEditPackageDropdown(true);
    } catch {
      setEditSearchResults([]);
      setShowEditPackageDropdown(true);
    } finally {
      setIsEditSearching(false);
    }
  };

  const handleEditSelectPackage = (result) => {
    setEditPackageId(result.packageId);
    setShowEditPackageDropdown(false);
    const versions = editSearchResults
      .filter((r) => r.packageId === result.packageId)
      .map((r) => ({
        versionCode: r.versionCode,
        contentVersion: r.contentVersion,
      }));
    setEditAvailableVersions(versions);
    setEditVersionCode("");
  };

  const handleEditVersionSelect = (versionCode) => {
    setEditVersionCode(String(versionCode));
    setShowEditVersionDropdown(false);
  };

  const handleEditSave = async () => {
    if (editIndex === null) return;
    const code = Number(editVersionCode.trim());
    if (!isValidPackageId(editPackageId)) return;

    const updatedDependencies = dependencies.map((dep, i) =>
      i === editIndex
        ? { packageId: editPackageId.trim(), versionCode: code }
        : dep,
    );
    try {
      await saveDependencies(updatedDependencies);
      setDependencies(updatedDependencies);
      setEditIndex(null);
    } catch (error) {
      setErrorMessage(
        error?.message || "Failed to update app dependency. Please try again.",
      );
      setOpenErrorPopup(true);
    }
  };

  const handleEditCancel = () => {
    setEditIndex(null);
  };

  const handleDelete = async (index) => {
    const updatedDependencies = dependencies.filter((_, i) => i !== index);
    try {
      await saveDependencies(updatedDependencies);
      setDependencies(updatedDependencies);
    } catch (error) {
      setErrorMessage(
        error?.message || "Failed to delete app dependency. Please try again.",
      );
      setOpenErrorPopup(true);
    }
  };

  // SDD57MARKETPLACE1389R121:
  // CODE57_MARKETPLACE_USTORY1389_R1-21: Add dependencies of an APK to other APK

  const saveDependencies = async (updatedDeps) => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    const contentId = sessionStorage.getItem("contentId");
    await APIManager.updatePortfolioContentAppDependency(
      portfolioId,
      contentId,
      updatedDeps,
    );
    if (onSave) onSave(updatedDeps);
  };

  // Combined table data
  const contentDeps = submissionData?.contentAppDependencyInfo || [];
  const allDeps = [
    ...dependencies.map((dep, index) => ({
      ...dep,
      source: "portfolio",
      index,
    })),
    ...contentDeps.map((dep) => ({ ...dep, source: "content" })),
  ];

  let versionInputValue = "";
  if (isManualEntry) {
    versionInputValue = manualVersionCode;
  } else if (selectedVersion !== null) {
    versionInputValue = `${selectedVersion}`;
  }

  return (
    <div className="oem-admin-app-dependency">
      <div className="oem-admin-app-dependency-title">App Dependency</div>
      <div className="oem-admin-app-dependency-subtitle">
        Specify any external or companion applications and the respective
        versions required for this app to function properly.
      </div>

      <div className="oem-admin-app-dependency-form">
        <div
          className="oem-admin-app-dependency-field"
          ref={packageDropdownRef}
        >
          <label htmlFor="oem-dep-package-id">Select Package</label>
          <div className="oem-admin-app-dependency-select-wrapper">
            <input
              id="oem-dep-package-id"
              type="text"
              placeholder="App Name/Title"
              value={packageId}
              onChange={handlePackageInputChange}
              onFocus={() => {
                if (packageId.length >= 2) setShowPackageDropdown(true);
              }}
              autoComplete="off"
              maxLength={200}
            />
            {isSearching ? (
              <span className="oem-admin-app-dependency-input-spinner"></span>
            ) : (
              <ArrowDropDownIcon className="oem-admin-app-dependency-dropdown-icon" />
            )}
          </div>
          {showPackageDropdown && searchResults.length > 0 && (
            <div className="oem-admin-app-dependency-dropdown">
              {[
                ...new Map(searchResults.map((r) => [r.packageId, r])).values(),
              ].map((result) => (
                <button
                  key={result.packageId}
                  type="button"
                  className="oem-admin-app-dependency-dropdown-item"
                  onClick={() => handleSelectPackage(result)}
                >
                  <span className="dropdown-title">{result.title}</span>
                  <span className="dropdown-package">{result.packageId}</span>
                </button>
              ))}
            </div>
          )}
          {isPackageInvalid && (
            <div className="oem-admin-helper-text">
              Please follow the valid format,{" "}
              <button
                type="button"
                className="oem-admin-helper-link"
                onClick={() => setShowPackageFormatInfo((prev) => !prev)}
              >
                click here
              </button>{" "}
              to see.
            </div>
          )}
          {isPackageInvalid && showPackageFormatInfo && (
            <div className="oem-admin-format-popover">
              <div className="oem-admin-format-title">Valid format</div>
              <ul>
                <li>At least two segments separated by dots (.)</li>
                <li>Each segment starts with a letter</li>
                <li>Only letters, numbers, and underscores</li>
                <li>No spaces or special characters</li>
              </ul>
            </div>
          )}
        </div>

        <div
          className="oem-admin-app-dependency-field"
          ref={versionDropdownRef}
        >
          <label htmlFor="oem-dep-version">App Version</label>
          <div className="oem-admin-app-dependency-select-wrapper">
            <input
              id="oem-dep-version"
              type="text"
              placeholder={
                isManualEntry ? "Enter version code" : "Select version"
              }
              value={versionInputValue}
              onChange={(e) => {
                if (isManualEntry) setManualVersionCode(e.target.value);
              }}
              onFocus={() => {
                if (!isManualEntry && availableVersions.length > 0)
                  setShowVersionDropdown(true);
                else if (
                  packageId.trim() !== "" &&
                  availableVersions.length === 0
                )
                  setIsManualEntry(true);
              }}
              readOnly={!isManualEntry}
              disabled={packageId.trim() === ""}
            />
            <ArrowDropDownIcon className="oem-admin-app-dependency-dropdown-icon" />
          </div>
          {showVersionDropdown && availableVersions.length > 0 && (
            <div className="oem-admin-app-dependency-dropdown">
              {availableVersions.map((ver) => (
                <button
                  key={ver.versionCode}
                  type="button"
                  className={`oem-admin-app-dependency-dropdown-item version-item ${selectedVersion === ver.versionCode ? "selected" : ""}`}
                  onClick={() => handleVersionSelect(ver.versionCode)}
                >
                  <span>{ver.versionCode}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          className="oem-admin-app-dependency-add-btn"
          onClick={handleAdd}
          disabled={!isAddEnabled || isSaving}
        >
          Add
        </button>
      </div>

      {allDeps.length > 0 && (
        <div className="oem-admin-app-dependency-table-container">
          <table className="oem-admin-app-dependency-table">
            <thead>
              <tr>
                <th>Package</th>
                <th>App Version</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {allDeps.map((dep, idx) => {
                const isEditing =
                  dep.source === "portfolio" && editIndex === dep.index;
                const isEditable = dep.source === "portfolio";

                if (isEditing) {
                  return (
                    <tr
                      key={`edit-${dep.index}`}
                      className="oem-admin-app-dependency-row-inline-edit"
                    >
                      <td>
                        <div
                          className="oem-admin-app-dependency-inline-field"
                          ref={editPackageDropdownRef}
                        >
                          <div className="oem-admin-app-dependency-inline-select-wrapper">
                            <input
                              type="text"
                              value={editPackageId}
                              onChange={handleEditPackageInputChange}
                              onFocus={() => {
                                if (editPackageId.length >= 2)
                                  setShowEditPackageDropdown(true);
                              }}
                              autoComplete="off"
                              placeholder="Package"
                            />
                            {isEditSearching ? (
                              <span className="oem-admin-app-dependency-input-spinner"></span>
                            ) : (
                              <ArrowDropDownIcon className="oem-admin-app-dependency-dropdown-icon" />
                            )}
                          </div>
                          {showEditPackageDropdown &&
                            editSearchResults.length > 0 && (
                              <div className="oem-admin-app-dependency-dropdown">
                                {[
                                  ...new Map(
                                    editSearchResults.map((r) => [
                                      r.packageId,
                                      r,
                                    ]),
                                  ).values(),
                                ].map((result) => (
                                  <button
                                    key={result.packageId}
                                    type="button"
                                    className="oem-admin-app-dependency-dropdown-item"
                                    onClick={() =>
                                      handleEditSelectPackage(result)
                                    }
                                  >
                                    <span className="dropdown-title">
                                      {result.title}
                                    </span>
                                    <span className="dropdown-package">
                                      {result.packageId}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            )}
                        </div>
                      </td>
                      <td>
                        <div
                          className="oem-admin-app-dependency-inline-field"
                          ref={editVersionDropdownRef}
                        >
                          <div className="oem-admin-app-dependency-inline-select-wrapper">
                            <input
                              type="text"
                              value={editVersionCode}
                              onChange={(e) =>
                                setEditVersionCode(e.target.value)
                              }
                              onFocus={() => {
                                if (editAvailableVersions.length > 0)
                                  setShowEditVersionDropdown(true);
                              }}
                              placeholder="Version"
                            />
                            <ArrowDropDownIcon className="oem-admin-app-dependency-dropdown-icon" />
                          </div>
                          {showEditVersionDropdown &&
                            editAvailableVersions.length > 0 && (
                              <div className="oem-admin-app-dependency-dropdown">
                                {editAvailableVersions.map((ver) => (
                                  <button
                                    key={ver.versionCode}
                                    type="button"
                                    className="oem-admin-app-dependency-dropdown-item version-item"
                                    onClick={() =>
                                      handleEditVersionSelect(ver.versionCode)
                                    }
                                  >
                                    <span>{ver.versionCode}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                        </div>
                      </td>
                      <td>
                        <div
                          className="oem-admin-app-dependency-actions"
                          style={{ marginRight: "5px" }}
                        >
                          <button
                            onClick={handleEditSave}
                            aria-label="Save"
                            className="oem-admin-app-dependency-action-save"
                            disabled={!isValidPackageId(editPackageId)}
                          >
                            <svg
                              width="16"
                              height="12"
                              viewBox="0 0 16 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M5.59509 11.8037L0 6.20859L1.39877 4.80982L5.59509 9.00613L14.6012 0L16 1.39877L5.59509 11.8037Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={handleEditCancel}
                            aria-label="Cancel"
                            className="oem-admin-app-dependency-action-cancel"
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1.40351 10L0 8.59649L3.60902 4.98747L0 1.40351L1.40351 0L5.01253 3.60902L8.59649 0L10 1.40351L6.39098 4.98747L10 8.59649L8.59649 10L5.01253 6.39098L1.40351 10Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={`${dep.source}-${dep.packageId}-${dep.versionCode}-${idx}`}
                  >
                    <td className="dep-text-cell dep-package-cell">
                      <span className="dep-cell-text" title={dep.packageId}>
                        {dep.packageId}
                      </span>
                    </td>
                    <td className="dep-text-cell dep-version-cell">
                      <span
                        className="dep-cell-text"
                        title={String(dep.versionCode)}
                      >
                        {dep.versionCode}
                      </span>
                    </td>
                    <td>
                      {isEditable && (
                        <div className="oem-admin-app-dependency-actions">
                          <button
                            onClick={() => handleEdit(dep.index)}
                            aria-label="Edit"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.4863 5.73699L10.2307 2.51507L11.3031 1.4411C11.5968 1.14703 11.9574 1 12.3851 1C12.8128 1 13.1735 1.14703 13.4671 1.4411L14.5396 2.51507C14.8332 2.80913 14.9864 3.16393 14.9992 3.57945C15.012 3.99498 14.8715 4.34977 14.5779 4.64384L13.4863 5.73699ZM12.3755 6.86849L4.25563 15H1V11.7397L9.11992 3.60822L12.3755 6.86849Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(dep.index)}
                            aria-label="Delete"
                          >
                            <svg
                              width="15"
                              height="16"
                              viewBox="0 0 15 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M2.66667 16C2.17778 16 1.75926 15.8259 1.41111 15.4778C1.06296 15.1296 0.888889 14.7111 0.888889 14.2222V2.66667H0V0.888889H4.44444V0H9.77778V0.888889H14.2222V2.66667H13.3333V14.2222C13.3333 14.7111 13.1593 15.1296 12.8111 15.4778C12.463 15.8259 12.0444 16 11.5556 16H2.66667ZM11.5556 2.66667H2.66667V14.2222H11.5556V2.66667ZM4.44444 12.4444H6.22222V4.44444H4.44444V12.4444ZM8 12.4444H9.77778V4.44444H8V12.4444Z"
                                fill="#121415"
                                fillOpacity="0.6"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage="Error"
        componentName="appError"
        onClose={() => setOpenErrorPopup(false)}
      />
    </div>
  );
}

OemAdminAppDependency.propTypes = {
  onSave: PropTypes.func,
};

export default OemAdminAppDependency;
